/**
 * PrintTechOverrideModal — 3PL client-catalog routing (/products).
 *
 * Sets the per-(sku, location) print-technology override on a CLIENT's SKUs at
 * one of the caller's shared locations. Deliberately separate from
 * `BatchEditVariantsModal`:
 *  - That modal edits the SKU record itself (title, cost, weight, the org-wide
 *    `skus.print_technology`). None of those are the 3PL's to change.
 *  - This one writes a routing decision scoped to the floor the 3PL runs. The
 *    client's catalog default is untouched, which is the single most important
 *    thing the operator has to understand before submitting, so it is stated
 *    in the body rather than left to a tooltip.
 *
 * Partial-failure contract mirrors `BatchEditVariantsModal`: chunks are
 * submitted sequentially, and a mid-loop failure reports "applied N of M",
 * keeps the modal open and the caller's selection intact (the write is
 * absolute, so re-submitting is idempotent).
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';

/** Router schema cap (`setPrintTechnologyOverrideSchema.skuIds` max 200). */
const SKU_CHUNK_SIZE = 200;

/** 'CLEAR' maps to a null `value` (drop the override, fall back to default). */
type TechChoice = 'DTG' | 'DTF' | 'CLEAR';

export interface PrintTechVariant {
  id: string;
  sku: string;
  /** 'POD' | 'PAP' | null. Non-POD rows are skipped server-side. */
  productionType?: string | null;
}

interface PrintTechOverrideModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetOrganization: { id: string; name: string };
  /** Locations whose junction authorises this cross-org view. */
  sharedLocations: { id: string; name: string }[];
  variants: PrintTechVariant[];
  onUpdated: () => void | Promise<void>;
}

export default function PrintTechOverrideModal({
  isOpen,
  onClose,
  targetOrganization,
  sharedLocations,
  variants,
  onUpdated,
}: PrintTechOverrideModalProps) {
  const { t } = useTranslation('inventory');
  const [locationId, setLocationId] = useState<string | null>(null);
  const [tech, setTech] = useState<TechChoice>('DTF');
  const [submitting, setSubmitting] = useState(false);

  const mutation = trpc.inventory.setPrintTechnologyOverride.useMutation();

  // One shared location is the common case (a 3PL floor with one warehouse):
  // preselect it rather than making the operator confirm the only answer.
  useEffect(() => {
    if (!isOpen) return;
    const only = sharedLocations.length === 1 ? sharedLocations[0] : undefined;
    if (only) setLocationId(only.id);
  }, [isOpen, sharedLocations]);

  const podIds = useMemo(
    () => variants.filter((v) => v.productionType === 'POD').map((v) => v.id),
    [variants],
  );
  const skippedPapCount = variants.length - podIds.length;
  const selectedLocation = sharedLocations.find((l) => l.id === locationId) ?? null;

  const handleClose = () => {
    setLocationId(null);
    setTech('DTF');
    setSubmitting(false);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!locationId || podIds.length === 0 || submitting) return;

    const value = tech === 'CLEAR' ? null : tech;
    const totals = { updatedCount: 0, seededCount: 0, skippedPapCount: 0 };
    let attempted = 0;

    setSubmitting(true);
    try {
      for (let i = 0; i < podIds.length; i += SKU_CHUNK_SIZE) {
        const chunk = podIds.slice(i, i + SKU_CHUNK_SIZE);
        const result = await mutation.mutateAsync({
          locationId,
          targetOrganizationId: targetOrganization.id,
          skuIds: chunk,
          value,
        });
        attempted += chunk.length;
        totals.updatedCount += result.updatedCount;
        totals.seededCount += result.seededCount;
        totals.skippedPapCount += result.skippedPapCount;
      }

      const description = [
        t('clientCatalog.toast.successCounts', {
          updated: totals.updatedCount,
          seeded: totals.seededCount,
        }),
        totals.skippedPapCount > 0
          ? t('clientCatalog.toast.successSkipped', { count: totals.skippedPapCount })
          : null,
      ]
        .filter(Boolean)
        .join(' ');

      toast.success(
        t('clientCatalog.toast.successTitle', {
          location: selectedLocation?.name ?? '',
        }),
        { description },
      );
      handleClose();
      await onUpdated();
    } catch (err) {
      const message = err instanceof Error && err.message ? err.message : '';
      if (attempted > 0) {
        // Earlier chunks persisted. Keep the modal AND the caller's selection
        // so submitting again finishes the remainder (writes are absolute).
        toast.error(
          t('clientCatalog.toast.partial', {
            applied: attempted,
            total: podIds.length,
            message,
          }),
        );
      } else {
        toast.error(message || t('clientCatalog.toast.failed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const noSharedLocations = sharedLocations.length === 0;
  const locationMissing = !locationId && !noSharedLocations;
  const canSubmit = !!locationId && podIds.length > 0 && !submitting;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {t('clientCatalog.modal.title', {
                count: variants.length,
                client: targetOrganization.name,
              })}
            </DialogTitle>
            <DialogDescription>
              {t('clientCatalog.modal.description', { client: targetOrganization.name })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {noSharedLocations ? (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                {t('clientCatalog.modal.noSharedLocations', { client: targetOrganization.name })}
              </p>
            ) : (
              <div className="grid gap-1.5">
                <Label htmlFor="print-tech-location" className="text-sm">
                  {t('clientCatalog.modal.locationLabel')}
                  <span aria-hidden="true" className="ml-0.5 text-destructive">
                    *
                  </span>
                </Label>
                <Select
                  value={locationId ?? undefined}
                  onValueChange={setLocationId}
                  disabled={submitting}
                >
                  <SelectTrigger
                    id="print-tech-location"
                    aria-required
                    aria-invalid={locationMissing}
                    aria-describedby={locationMissing ? 'print-tech-location-helper' : undefined}
                    className={cn(
                      'transition-colors motion-reduce:transition-none',
                      locationMissing &&
                        'border-amber-300 ring-1 ring-amber-100/50 focus:ring-amber-200',
                    )}
                  >
                    <SelectValue placeholder={t('clientCatalog.modal.locationPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {sharedLocations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {locationMissing && (
                  <p id="print-tech-location-helper" className="text-xs text-amber-700">
                    {t('clientCatalog.modal.locationRequired')}
                  </p>
                )}
              </div>
            )}

            <div className="grid gap-1.5">
              <Label htmlFor="print-tech-value" className="text-sm">
                {t('clientCatalog.modal.techLabel')}
              </Label>
              <Select
                value={tech}
                onValueChange={(next) => setTech(next as TechChoice)}
                disabled={submitting || noSharedLocations}
              >
                <SelectTrigger id="print-tech-value">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DTG">DTG</SelectItem>
                  <SelectItem value="DTF">DTF</SelectItem>
                  <SelectItem value="CLEAR">
                    {t('clientCatalog.modal.techClear', { client: targetOrganization.name })}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <p className="text-xs text-muted-foreground">
              {selectedLocation
                ? t('clientCatalog.modal.note', {
                    location: selectedLocation.name,
                    client: targetOrganization.name,
                  })
                : t('clientCatalog.modal.noteNoLocation', { client: targetOrganization.name })}
            </p>

            {podIds.length === 0 ? (
              <p className="text-xs text-amber-700">
                {t('clientCatalog.modal.noPodSelected')}
              </p>
            ) : (
              skippedPapCount > 0 && (
                <p className="text-xs text-muted-foreground">
                  {t('clientCatalog.modal.papSkipped', { count: skippedPapCount })}
                </p>
              )
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('clientCatalog.modal.applying')}
                </>
              ) : (
                t('clientCatalog.modal.apply', { count: podIds.length })
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
